import { useAuth } from '../../../lib/auth';
import AuthLayout from '../../layouts/AuthLayout';
import clsx from 'clsx';
import { useFormik } from 'formik';
import api from '../../../lib/api';
import FormInput from '../../../components/forms/FormInput.tsx';
import { Button } from '../../../components/buttons/Button.tsx';
import Alert from '../../../components/Alert.tsx';
import { useState } from 'react';
import { transformLaravelToFormikErrors } from '../../../lib/helpers.ts';
import {
    FormResultAlertProps,
    SchemaDrivenFormFieldProps,
} from '../../../lib/types.ts';

function PasswordChangeSection() {
    const [formResult, setFormResult] = useState<FormResultAlertProps>({
        type: undefined,
        message: undefined,
    });

    const {
        handleSubmit,
        errors,
        touched,
        handleChange,
        values,
        isSubmitting,
        setSubmitting,
        setErrors,
        setValues,
    } = useFormik({
        initialValues: {
            current_password: '',
            new_password: '',
            new_password_confirmation: '',
        },
        onSubmit: (values) => {
            setFormResult({ type: undefined, message: undefined });

            api.patch('/user/password', values)
                .then((res) => {
                    setFormResult({
                        type: 'success',
                        message: res?.data,
                    });

                    setValues({
                        current_password: '',
                        new_password: '',
                        new_password_confirmation: '',
                    });
                })
                .catch((err) => {
                    const message = err?.response?.data?.message;

                    setFormResult({
                        type: 'error',
                        message: message,
                    });

                    const errors = err?.response?.data?.errors;
                    if (errors)
                        setErrors(transformLaravelToFormikErrors(errors));
                })
                .finally(() => {
                    setSubmitting(false);
                });
        },
    });

    const schema: SchemaDrivenFormFieldProps[] = [
        {
            name: 'current_password',
            type: 'password',
            label: 'Současné heslo',
        },
        {
            name: 'new_password',
            type: 'password',
            label: 'Nové heslo',
        },
        {
            name: 'new_password_confirmation',
            type: 'password',
            label: 'Nové heslo znovu',
        },
    ];

    return (
        <div>
            <h2>Uživatelský profil</h2>
            <div className="rounded-2xl bg-white px-8 py-6 shadow-sm">
                <h3 className="mb-4">Změna hesla</h3>

                {formResult.type && formResult.message && (
                    <Alert type={formResult.type}>{formResult.message}</Alert>
                )}

                <form
                    className="flex flex-col space-y-6"
                    onSubmit={handleSubmit}
                >
                    {schema.map(({ type, name, label, placeholder }) => (
                        <FormInput
                            key={name}
                            name={name}
                            label={label!}
                            onChange={handleChange}
                            value={values[name as keyof typeof values]}
                            type={type}
                            error={
                                errors[name as keyof typeof errors] &&
                                touched[name as keyof typeof touched]
                                    ? errors[name as keyof typeof errors]
                                    : null
                            }
                            placeholder={placeholder}
                            required={true}
                        />
                    ))}
                    <Button
                        type="submit"
                        color="product"
                        className="mt-3 self-end"
                        loadingText="Probíhá změna"
                        isLoading={isSubmitting}
                    >
                        Změnit heslo
                    </Button>
                </form>
            </div>
        </div>
    );
}

export default function UserSettingsPage() {
    useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'UserSettingsPage',
    });

    return (
        <AuthLayout>
            <main
                className={clsx(
                    'relative',
                    'mx-auto box-content max-w-2xl py-9',
                    'px-4 sm:px-8 md:px-10'
                )}
            >
                <div className="mb-14">
                    <h1>Nastavení</h1>
                </div>
                <PasswordChangeSection />
            </main>
        </AuthLayout>
    );
}
